import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EVENT_TYPE_OPTIONS, HOURS_OPTIONS, HEARD_OPTIONS } from "@/data/content";
import { estimateTravel, BASE } from "@/lib/travel";
import { trackEvent } from "@/lib/analytics";
import { Check, MapPin } from "lucide-react";

const empty = {
  name: "", email: "", phone: "", event_date: "", event_type: "",
  venue_city: "", postal_code: "", estimated_hours: "", heard_from: "", message: "",
};

export const LeadForm = ({ dark = false }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [travel, setTravel] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onPostal = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, postal_code: v }));
    setTravel(estimateTravel(v));
  };

  const travelSummary = () => {
    if (!travel || travel.unknown) return "";
    return `${travel.city} · ~${travel.distanceKm}km from base · est. travel ${travel.fee === 0 ? "included" : "$" + travel.fee}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "event_date", "event_type", "venue_city", "postal_code", "estimated_hours"];
    for (const k of required) {
      if (!form[k]) { toast.error("Please fill in all required fields."); return; }
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, travel_estimate: travelSummary() });
      setDone(true);
      trackEvent("generate_lead", {
        event_category: "booking",
        event_label: form.event_type,
        event_city: form.venue_city,
        value: 1,
        currency: "CAD",
      });
      toast.success("Got it! Check your inbox — we'll reply within 24 hours.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = dark ? "text-cream/50" : "text-ink/50";
  const inputExtra = dark ? "text-cream placeholder:text-cream/30 !border-cream/25 focus:!border-cream" : "";

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl border p-10 text-center ${dark ? "border-cream/20 text-cream" : "border-clubborder text-ink"}`}
        data-testid="lead-form-success"
      >
        <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-6 ${dark ? "bg-cream text-forest" : "bg-forest text-cream"}`}>
          <Check size={26} />
        </div>
        <h3 className="font-serif text-4xl mb-3">You're on the list.</h3>
        <p className={dark ? "text-cream/70" : "text-ink/60"}>
          We've sent a confirmation to <strong>{form.email}</strong>. I'll personally reply within 24 hours with availability and a quote.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
        <Field label="Full name *" labelCls={labelCls}>
          <input data-testid="lead-name" className={`ledger-input ${inputExtra}`} value={form.name} onChange={set("name")} placeholder="Jamie Rivera" />
        </Field>
        <Field label="Email *" labelCls={labelCls}>
          <input data-testid="lead-email" type="email" className={`ledger-input ${inputExtra}`} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </Field>
        <Field label="Phone *" labelCls={labelCls}>
          <input data-testid="lead-phone" className={`ledger-input ${inputExtra}`} value={form.phone} onChange={set("phone")} placeholder="(604) 555-0123" />
        </Field>
        <Field label="Event date *" labelCls={labelCls}>
          <input data-testid="lead-date" type="date" className={`ledger-input ${inputExtra}`} value={form.event_date} onChange={set("event_date")} />
        </Field>
        <Field label="Event type *" labelCls={labelCls}>
          <select data-testid="lead-type" className={`ledger-input ${inputExtra}`} value={form.event_type} onChange={set("event_type")}>
            <option value="">Select…</option>
            {EVENT_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Estimated hours *" labelCls={labelCls}>
          <select data-testid="lead-hours" className={`ledger-input ${inputExtra}`} value={form.estimated_hours} onChange={set("estimated_hours")}>
            <option value="">Select…</option>
            {HOURS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Venue address *" labelCls={labelCls}>
          <input data-testid="lead-city" className={`ledger-input ${inputExtra}`} value={form.venue_city} onChange={set("venue_city")} placeholder="Venue name / address, city" />
        </Field>
        <Field label="Postal code *" labelCls={labelCls}>
          <input data-testid="lead-postal" className={`ledger-input ${inputExtra}`} value={form.postal_code} onChange={onPostal} placeholder="V5C 0A1" />
        </Field>
        <Field label="How did you hear about us?" labelCls={labelCls}>
          <select data-testid="lead-heard" className={`ledger-input ${inputExtra}`} value={form.heard_from} onChange={set("heard_from")}>
            <option value="">Select…</option>
            {HEARD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Anything else?" labelCls={labelCls}>
          <input data-testid="lead-message" className={`ledger-input ${inputExtra}`} value={form.message} onChange={set("message")} placeholder="Venue name, theme, questions…" />
        </Field>
      </div>

      {travel && (
        <div
          data-testid="travel-estimate"
          className={`rounded-xl border p-4 flex items-start gap-3 ${dark ? "border-cream/20 bg-cream/5 text-cream" : "border-clubborder bg-clubmuted/60 text-ink"}`}
        >
          <MapPin size={18} className={dark ? "text-cream/70 mt-0.5 shrink-0" : "text-forest mt-0.5 shrink-0"} />
          <div className="text-sm">
            {travel.unknown ? (
              <span className={dark ? "text-cream/70" : "text-ink/60"}>We serve the whole Lower Mainland — we'll confirm any travel fee in your quote.</span>
            ) : travel.withinRadius ? (
              <span><strong>{travel.city}</strong> is within our 30km radius — <strong>travel included</strong>, no extra charge.</span>
            ) : (
              <span>
                <strong>{travel.city}</strong> is ~{travel.distanceKm}km from our {BASE.name} base.
                Estimated travel: <strong>${travel.fee}</strong>{travel.capped ? " (capped)" : ""}.
                <span className={dark ? "text-cream/50" : "text-ink/50"}> Final quote confirmed by us.</span>
              </span>
            )}
          </div>
        </div>
      )}

      <p className={`text-xs ${dark ? "text-cream/40" : "text-ink/40"}`}>
        Flat pricing within 30km of our base. Beyond that, travel is $5/km (capped at $200) — always quoted upfront.
      </p>

      <motion.button
        type="submit" disabled={loading} data-testid="lead-submit"
        whileHover={{ scale: 0.985 }} whileTap={{ scale: 0.96 }}
        className={`w-full sm:w-auto rounded-full px-10 py-4 text-base font-medium disabled:opacity-60 transition-colors ${
          dark ? "bg-cream text-forest-deep hover:bg-sand" : "bg-forest text-cream hover:bg-forest-deep"
        }`}
      >
        {loading ? "Sending…" : "Check my date"}
      </motion.button>
    </form>
  );
};

const Field = ({ label, labelCls, children }) => (
  <label className="block">
    <span className={`block text-xs uppercase tracking-[0.14em] mb-1 ${labelCls}`}>{label}</span>
    {children}
  </label>
);
